/**
 * Domain: Competency business rules and mappings.
 * Pure functions — no side effects, no Supabase, no React.
 */

import type { ProficiencyLevel } from "@/data/competencyProfilesData";

// ─── Status / Level Mappings ─────────────────────────────────────────

export type CompetencyAssessmentStatus = "not_met" | "in_evolution" | "almost_achieved" | "achieved";

/** Map a 0-3 survey score to a proficiency level string. */
export const LEVEL_MAP: Record<number, string> = {
  0: "junior",
  1: "evolution",
  2: "satisfactory",
  3: "advanced",
};

/** Map a 0-3 survey score to a DB assessment status. */
export const STATUS_MAP: Record<number, string> = {
  0: "not_met",
  1: "in_evolution",
  2: "almost_achieved",
  3: "achieved",
};

export const REVERSE_STATUS_MAP: Record<string, number> = {
  not_met: 0,
  in_evolution: 1,
  almost_achieved: 2,
  achieved: 3,
};

/** UI key ↔ DB status mapping */
export const uiToDbStatus: Record<string, CompetencyAssessmentStatus> = {
  notMet: "not_met",
  evolution: "in_evolution",
  almostAchieved: "almost_achieved",
  achieved: "achieved",
};

export const dbToUiStatus: Record<CompetencyAssessmentStatus, string> = {
  not_met: "notMet",
  in_evolution: "evolution",
  almost_achieved: "almostAchieved",
  achieved: "achieved",
};

// ─── Proficiency Averaging ───────────────────────────────────────────

export const PROFICIENCY_NUMERIC: Record<string, number> = {
  junior: 1,
  evolution: 2,
  satisfactory: 3,
  satisfaisant: 3,
  advanced: 4,
  avance: 4,
  expert: 5,
};

const NUMERIC_TO_PROFICIENCY = ["junior", "evolution", "satisfactory", "advanced", "expert"];

/** Convert a numeric average back to a proficiency level label. */
export function numericToLevel(avg: number): string {
  const rounded = Math.round(avg);
  const clamped = Math.max(1, Math.min(5, rounded));
  return NUMERIC_TO_PROFICIENCY[clamped - 1];
}

// ─── UI Status ↔ Display Level Mappings ──────────────────────────────

/** Map UI proficiency status (notMet/evolution/almostAchieved/achieved) → display level (junior/evolution/satisfaisant/avance). */
export const UI_STATUS_TO_DISPLAY_LEVEL: Record<string, string> = {
  notMet: "junior",
  evolution: "evolution",
  almostAchieved: "satisfaisant",
  achieved: "avance",
};

/** Map display level (junior/evolution/satisfaisant/avance/expert) → numeric for averaging. */
export const DISPLAY_LEVEL_NUMERIC: Record<string, number> = {
  junior: 1,
  evolution: 2,
  satisfaisant: 3,
  avance: 4,
  expert: 5,
};

const NUMERIC_TO_DISPLAY_LEVEL = ["junior", "evolution", "satisfaisant", "avance", "expert"];

/** Convert numeric average to display level string (FR-style: satisfaisant, avance). */
export function numericToDisplayLevel(avg: number): string {
  const rounded = Math.round(avg);
  const clamped = Math.max(1, Math.min(5, rounded));
  return NUMERIC_TO_DISPLAY_LEVEL[clamped - 1];
}

/** Map survey answer index (0-3) → UI status key. */
export const SURVEY_INDEX_TO_UI_STATUS: Record<number, string> = {
  0: "notMet",
  1: "evolution",
  2: "almostAchieved",
  3: "achieved",
};

export interface CompetencyScore {
  competency_id: string;
  assessed_level: string;
  status: CompetencyAssessmentStatus | null;
}

export interface AveragedCompetencyScore {
  competencyId: string;
  averageLevel: string;
  averageStatus: string; // UI status key
  count: number;
}

/** Compute averaged scores per competency from a list of raw scores. Pure function. */
export function computeAveragedScores(scores: CompetencyScore[]): Map<string, AveragedCompetencyScore> {
  const map = new Map<string, AveragedCompetencyScore>();
  const grouped = new Map<string, CompetencyScore[]>();

  for (const score of scores) {
    const existing = grouped.get(score.competency_id) || [];
    existing.push(score);
    grouped.set(score.competency_id, existing);
  }

  const STATUS_NUMERIC: Record<string, number> = {
    not_met: 1,
    in_evolution: 2,
    almost_achieved: 3,
    achieved: 4,
  };
  const NUMERIC_TO_STATUS = ["not_met", "in_evolution", "almost_achieved", "achieved"];

  for (const [competencyId, groupScores] of grouped) {
    const levelNums = groupScores
      .map((s) => PROFICIENCY_NUMERIC[s.assessed_level])
      .filter((n) => n !== undefined);

    const avgLevel =
      levelNums.length > 0
        ? numericToLevel(levelNums.reduce((a, b) => a + b, 0) / levelNums.length)
        : "junior";

    const statusNums = groupScores
      .filter((s) => s.status)
      .map((s) => STATUS_NUMERIC[s.status!])
      .filter((n) => n !== undefined);

    let avgStatusDb: CompetencyAssessmentStatus = "not_met";
    if (statusNums.length > 0) {
      const avgNum = Math.round(statusNums.reduce((a, b) => a + b, 0) / statusNums.length);
      avgStatusDb = (NUMERIC_TO_STATUS[Math.max(0, Math.min(3, avgNum - 1))] || "not_met") as CompetencyAssessmentStatus;
    }

    map.set(competencyId, {
      competencyId,
      averageLevel: avgLevel,
      averageStatus: dbToUiStatus[avgStatusDb] || "notMet",
      count: groupScores.length,
    });
  }

  return map;
}

// ─── Competency Distribution Algorithm ───────────────────────────────

interface CompetencyItem {
  id: string;
  name: string;
  description: string;
}

interface CompetencyGroup {
  id: string;
  name: string;
  competencies: CompetencyItem[];
}

/**
 * Distribute competencies across groups with a target total count,
 * spreading evenly then filling deficit from groups with remaining capacity.
 */
export function distributeCompetenciesAcrossGroups(
  groups: CompetencyGroup[],
  maxGroups: number,
  maxCompetencies: number,
): CompetencyGroup[] {
  const selectedGroups = groups.slice(0, maxGroups);
  if (selectedGroups.length === 0) return [];

  const base = Math.floor(maxCompetencies / selectedGroups.length);
  const remainder = maxCompetencies % selectedGroups.length;

  const perGroupTarget = selectedGroups.map((_, idx) => base + (idx < remainder ? 1 : 0));
  const pickedCounts = selectedGroups.map(() => 0);
  const maxCounts = selectedGroups.map((g) => g.competencies.length);

  perGroupTarget.forEach((target, groupIdx) => {
    pickedCounts[groupIdx] = Math.min(target, maxCounts[groupIdx]);
  });

  let pickedTotal = pickedCounts.reduce((sum, n) => sum + n, 0);
  let deficit = Math.max(0, maxCompetencies - pickedTotal);

  while (deficit > 0) {
    let madeProgress = false;
    for (let i = 0; i < selectedGroups.length && deficit > 0; i++) {
      if (pickedCounts[i] < maxCounts[i]) {
        pickedCounts[i] += 1;
        deficit -= 1;
        madeProgress = true;
      }
    }
    if (!madeProgress) break;
  }

  return selectedGroups.map((g, idx) => ({
    ...g,
    competencies: g.competencies.slice(0, pickedCounts[idx]),
  }));
}

// ─── Survey Answer Filtering ─────────────────────────────────────────

/** Filter survey answers to only keep keys matching allowed competency IDs plus value question keys. */
export function filterSurveyAnswers(
  raw: Record<string, number>,
  allowedIds: string[],
): Record<string, number> {
  const allowedSet = new Set(allowedIds);
  const filteredAnswers: Record<string, number> = {};
  for (const [answerKey, score] of Object.entries(raw)) {
    if (allowedSet.has(answerKey) || answerKey.startsWith("value-")) {
      filteredAnswers[answerKey] = score;
    }
  }
  return filteredAnswers;
}

/** Check if a string is a valid UUID */
export function isUUID(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}
